// 案 A 用 フィルタ drawer (右からスライドイン)
// PC 版 FilterBar の主要項目を モバイル向けに drawer 化
import { useEffect } from "react";
import type { Iryo, Yotei, Granularity, MetaResponse } from "@shared/api";

export interface MobileFilterState {
  granularity: Granularity;
  iryo: Iryo;
  yotei: Yotei;
  asOfMonth: string;          // YYYY-MM-DD
  focusedDept: string | null; // null = 病院全体
}

interface Props {
  open: boolean;
  onClose: () => void;
  state: MobileFilterState;
  setState: (s: MobileFilterState) => void;
  meta: MetaResponse;
}

const GRAN_OPTS: Granularity[] = ["大", "中", "小"];
const IRYO_OPTS: Iryo[] = ["合計", "急性期", "回復期"];
const YOTEI_OPTS: Yotei[] = ["合計", "予定", "緊急"];

export function FilterDrawer({ open, onClose, state, setState, meta }: Props) {
  // 直近 12 ヶ月の月リスト
  const monthOptions: string[] = [];
  for (let i = 0; i < 12; i++) {
    monthOptions.push(monthShift(meta.dateMax, -i));
  }

  // 粒度に応じた科リスト
  const deptsForGran = meta.deptsAll
    .filter((d) => d.粒度 === state.granularity && !d.is_total)
    .map((d) => d.診療科);

  // ESC で閉じる
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // body scroll lock
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900 z-40 transition-opacity duration-200 ${
          open ? "opacity-40 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      />
      {/* drawer (右からスライドイン、 max-w-[85%] で iPhone でも背景が少し見える) */}
      <aside
        className={`fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 shadow-2xl flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
      >
        <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">⚙ フィルタ</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2 py-0.5 rounded active:bg-slate-100"
            aria-label="閉じる"
          >×</button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">
          <Field label="対象月">
            <select
              value={state.asOfMonth}
              onChange={(e) => setState({ ...state, asOfMonth: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>{m.slice(0, 7)}</option>
              ))}
            </select>
          </Field>

          <Field label="診療科粒度">
            <SegBtn
              options={GRAN_OPTS}
              value={state.granularity}
              onChange={(v) => setState({ ...state, granularity: v, focusedDept: null })}
            />
          </Field>

          <Field label="医療区分">
            <SegBtn
              options={IRYO_OPTS}
              value={state.iryo}
              onChange={(v) => setState({ ...state, iryo: v })}
            />
          </Field>

          <Field label="予定 / 緊急">
            <SegBtn
              options={YOTEI_OPTS}
              value={state.yotei}
              onChange={(v) => setState({ ...state, yotei: v })}
            />
          </Field>

          <Field label="🎯 診療科フォーカス">
            <select
              value={state.focusedDept ?? ""}
              onChange={(e) => setState({ ...state, focusedDept: e.target.value || null })}
              className="w-full border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">— 病院全体 —</option>
              {deptsForGran.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <div className="text-[10px] text-slate-400 mt-1">
              フォーカス中は 主要指標 + 危険信号が当該科に絞られます
            </div>
          </Field>
        </div>

        <footer className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <button
            type="button"
            onClick={() => setState({
              granularity: "小", iryo: "合計", yotei: "合計",
              asOfMonth: meta.dateMax, focusedDept: null,
            })}
            className="flex-1 text-xs py-2 border border-slate-300 rounded text-slate-600 active:bg-slate-100"
          >初期値に戻す</button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-xs py-2 bg-slate-900 text-white rounded font-semibold active:bg-slate-800"
          >適用して閉じる</button>
        </footer>
      </aside>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-700 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function SegBtn<T extends string>({ options, value, onChange }: {
  options: readonly T[]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="grid grid-flow-col auto-cols-fr gap-1 bg-slate-100 rounded p-1">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`text-xs py-1.5 rounded transition-colors ${
            value === opt
              ? "bg-white text-slate-900 font-semibold shadow-sm"
              : "text-slate-500 active:bg-slate-200"
          }`}
        >{opt}</button>
      ))}
    </div>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
